import React, { useCallback, useEffect, useState } from "react";

import { useScreenWidth, getKeyCode, colors } from "oolib";
import FilterModule from "../../../../components/discovery/FilterModule";
import { useGetQueryData } from "../../../../utils/react-query-hooks/general";
import {
  useGetDataLazy
} from "../../../../utils/react-query-hooks/getData";
import ImageModal from "./comps/ImageModal";
import { StyledWrapper } from "./styled";
import { getTenant } from "@/TENANT_CONFIGS";
import { getTenantConfig } from "./config";
import { useElemInView } from "@/utils/customHooks/useElemInView";
import MediaGrid from "./comps/MediaGrid";
import { prepareData } from "./utils/prepareData";

const MediaLibrary = (props) => {
  const tenantConfig = getTenantConfig(getTenant());

  const [activeFilters, setActiveFilters] = useState([]);
  const contentTypes = tenantConfig.contentTypes;

  const [mediaCollection, setMediaCollection] = useState([]);
  const { greyColor10 } = colors;

  const contentTypeFilterValues =
    activeFilters
      .find((filter) => filter?.filterId === "contentType")
      ?.values?.map((config) => config.value) ?? undefined;

  const { data, status, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetDataLazy({
      contentTypes: contentTypeFilterValues || contentTypes,
      findQuery: { kp_published_status: "published" },
      activeFilters,
      limit: 10,
      projection: { ...tenantConfig.projections, kp_date_published: 1 },
    });

  const { observerRef } = useElemInView({
    callback: fetchNextPage,
    enabled: !!hasNextPage && !isFetchingNextPage,
  });
  const [showModal, setShowModal] = useState(false);
  const screenWidth = useScreenWidth();
  const allTpls = useGetQueryData("allTpls");
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const handleKeyDown = (event) => {
      let code = getKeyCode(event);
      if (code === "ArrowRight") {
        setActiveIndex((prevIndex) => (prevIndex + 1) % mediaCollection.length);
      } else if (code === "ArrowLeft") {
        setActiveIndex(
          (prevIndex) =>
            (prevIndex - 1 + mediaCollection.length) % mediaCollection.length
        );
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeIndex]);

  const injectContentTypeOptions = ({
    filterConfig,
    allTpls,
    contentTypes,
  }) => {
    return filterConfig.map((filter) => {
      if (filter.filterId === "contentType") {
        filter.source.options = allTpls
          .filter((d) => contentTypes.includes(d.kp_content_type))
          .map((d) => ({
            display: d.general.content.title,
            value: d.kp_content_type,
          }));
      }
      return filter;
    });
  };

  const handleItemClick = useCallback((index) => {
    setShowModal(true);
    setActiveIndex(index);
  }, []);

  useEffect(() => {
    setMediaCollection(
      data
        ? prepareData({
            data,
            activeFilters,
            fieldConfig: tenantConfig.fieldConfig,
          })
        : []
    );
  }, [activeFilters, status, data]);

  return (
    <StyledWrapper>
      <ImageModal
        id={mediaCollection[activeIndex]?.name || `ImageModal_${mediaCollection?.[activeIndex]?.id}`} // name first because id can be same for all images if they are from same imageinput comp (multi)
        showModal={showModal}
        onClose={() => setShowModal(false)}
        activeIndex={activeIndex}
        onPrevious={() =>
          setActiveIndex(
            (prevIndex) =>
              (prevIndex - 1 + mediaCollection.length) % mediaCollection.length
          )
        }
        onNext={() =>
          setActiveIndex(
            (prevIndex) => (prevIndex + 1) % mediaCollection.length
          )
        }
        mediaCollection={mediaCollection}
        screenWidth={screenWidth}
        allTpls={allTpls}
        fieldConfig={tenantConfig.fieldConfig}
      />
      <div
        style={{ maxWidth: "30rem", borderRight: `1px solid ${greyColor10}` }}
      >
        <FilterModule
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          filterConfig={injectContentTypeOptions({
            filterConfig: tenantConfig.filterConfig,
            allTpls,
            contentTypes,
          })}
          // headerRef={headerRef}
          usedIn={"Gelabs Media Library"}
        />
      </div>
      <MediaGrid
        observerRef={hasNextPage ? observerRef : null}
        isFetchingNextPage={isFetchingNextPage}
        status={status}
        data={mediaCollection}
        onItemClick={handleItemClick}
      />
    </StyledWrapper>
  );
};

export default MediaLibrary;
